export const VISITA_SYNC_IDLE_MS = 3000;
export const VISITA_SYNC_MAX_WAIT_MS = 15000;

// Agrupa solicitudes de envío; nunca retrasa ni modifica las escrituras locales.
export function createVisitaSyncScheduler({ run, isOnline = () => true, onError = () => {},
    setTimer = setTimeout, clearTimer = clearTimeout,
    idleMs = VISITA_SYNC_IDLE_MS, maxWaitMs = VISITA_SYNC_MAX_WAIT_MS,
    retryDelays = [5000, 15000, 45000], random = Math.random, now = Date.now, budgetMs = 120000 }) {
    let idleTimer = null;
    let maxTimer = null;
    let pending = false;
    let retryFailed = false;
    let disposed = false;
    let running = false;
    let retries = 0;
    let retryTimer = null;
    let exhausted = false;
    let cycleStarted = null;
    let newCycleRequested = false;

    const clearTimers = () => {
        if (idleTimer !== null) clearTimer(idleTimer);
        if (maxTimer !== null) clearTimer(maxTimer);
        if (retryTimer !== null) clearTimer(retryTimer);
        idleTimer = maxTimer = retryTimer = null;
    };

    const cancel = () => {
        clearTimers();
        pending = false;
        retryFailed = false;
    };
    const flush = () => {
        if (disposed || !pending || running) return;
        clearTimers();
        if (!isOnline()) return; // El dato durable y el despertar siguen pendientes.
        if (cycleStarted === null) cycleStarted = now();
        const options = { retryFailed, newCycle: newCycleRequested };
        newCycleRequested = false;
        pending = retryFailed = false;
        running = true;
        let retry = false;
        let retryAfter = 0;
        void Promise.resolve().then(() => { if (!disposed) return run(options); })
            .then(result => { retry = result?.retryable === true; retryAfter = Number(result?.retryAfterMs) || 0; })
            .catch(error => {
                const status = error?.response?.status;
                retry = error?.code === 'ERR_NETWORK' || error?.name === 'TypeError'
                    || [408, 423, 429, 500, 502, 503, 504].includes(status);
                const header = error?.response?.headers?.['retry-after'];
                retryAfter = /^\d+$/.test(String(header)) ? Number(header) * 1000 : Math.max(0, Date.parse(header) - now()) || 0;
                onError(error);
            }).finally(() => {
                running = false;
                if (disposed) return;
                if (pending && !retry) { flush(); return; }
                if (retry && retries < retryDelays.length) {
                    const wait = Math.max(retryAfter, Math.floor(retryDelays[retries++] * (0.9 + random() * 0.2)));
                    if (now() - cycleStarted + wait > budgetMs) { exhausted = true; pending = false; return; }
                    pending = true;
                    retryTimer = setTimer(flush, wait);
                } else if (!retry) { retries = 0; exhausted = false; cycleStarted = null; }
                else { exhausted = true; pending = false; }
            });
    };
    const schedule = (options = {}) => {
        if (disposed) return;
        if (options.newCycle === true && !running) { retries = 0; exhausted = false; cycleStarted = null; }
        newCycleRequested ||= options.newCycle === true;
        if (exhausted) return;
        pending = true;
        retryFailed ||= options.retryFailed === true;
        if (running || retryTimer !== null) return;
        if (options.immediate === true || retryFailed) {
            flush();
            return;
        }
        if (idleTimer !== null) clearTimer(idleTimer);
        idleTimer = setTimer(flush, idleMs);
        if (maxTimer === null) maxTimer = setTimer(flush, maxWaitMs);
    };

    return { schedule, cancel, hasPending: () => pending,
        dispose: () => { disposed = true; cancel(); } };
}
